import { ref } from "vue";
import usePixel from "~/hooks/usePixel";
import { getChannel } from "./getChannel";

export const isSandbox =
	process.env.NODE_ENV === "development" ||
	process.env.NUXT_PUBLIC_ENV === "development" ||
	(typeof window !== "undefined" &&
		/sandbox|localhost|127\.0\.0\.1/.test(window.location.hostname));

/** 开放手机号登录的域名（hostname 包含以下任一即开放，后续新增域名在此追加） */
export const PHONE_LOGIN_ALLOWED_HOSTS = ["seeronnet.com", "seeronnet.net", "seerq.io"];

export function isPhoneLoginAllowedHost(hostname) {
	const host = String(hostname || "").toLowerCase();
	return PHONE_LOGIN_ALLOWED_HOSTS.some(domain => host.includes(domain));
}

/** 当前环境是否展示手机号登录（仅客户端；测服/本地始终开放） */
export function canShowPhoneLogin() {
	if (!import.meta.client) return false;
	return isPhoneLoginAllowedHost(window.location.hostname) || isSandbox;
}

export function parseUA() {
	if (!import.meta.client) return {}; // SSR 返回空对象

	const u = navigator.userAgent;
	const u2 = navigator.userAgent.toLowerCase();

	return {
		trident: u.indexOf("Trident") > -1,
		presto: u.indexOf("Presto") > -1,
		webKit: u.indexOf("AppleWebKit") > -1,
		gecko: u.indexOf("Gecko") > -1 && u.indexOf("KHTML") == -1,
		mobile: !!u.match(/AppleWebKit.*Mobile.*/),
		ios: !!u.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/),
		android: u.indexOf("Android") > -1 || u.indexOf("Linux") > -1,
		iPhone: u.indexOf("iPhone") > -1,
		iPad: u.indexOf("iPad") > -1,
		webApp: u.indexOf("Safari") == -1,
		iosv: u.substr(u.indexOf("iPhone OS") + 9, 3),
		weixin: u2.match(/MicroMessenger/i) == "micromessenger",
		wxMiniprogram:
			(u.match(/MicroMessenger/i) && u.match(/miniprogram/i)) ||
			window.__wxjs_environment === "miniprogram",
		ali: u.indexOf("AliApp") > -1,
		weibo: u2.match(/WeiBo/i) == "weibo",
		baidu: u.indexOf("Baidu") > -1 || u.indexOf("BIDUBrowser") > -1,
	};
}

export const DownloadUrl = {
	android: "https://play.google.com/store/apps/details?id=com.mmc.seer.onnet",
	ios: "https://apps.apple.com/app/id6455938273",
	iosHk: "https://apps.apple.com/hk/app/id6455938273",
};

export function downloadApp(type) {
	if (!import.meta.client) return;

	const res = parseUA();
	let downLink = DownloadUrl.iosHk;

	if (type === "android" || res.android) {
		downLink = DownloadUrl.android;
	}
	if (type === "ios") {
		downLink = DownloadUrl.iosHk;
	}

	const { reportGooglePixelEvent } = usePixel();
	reportGooglePixelEvent("seer_app_download");

	const token = import.meta.client ? localStorage.getItem("token") : null;

	if (token) {
		const copyText = `SEER#TOKEN=${token}`;
		import("./clipboard.js").then(({ copy }) => {
			copy(
				copyText,
				"",
				() => window.open(downLink, "_blank"),
				() => window.open(downLink, "_blank")
			);
		});
	} else {
		window.open(downLink, "_blank");
	}
}

export const formatUrlParams = url => {
	if (!url) return "";
	return encodeURIComponent(url);
};

export const parseUrlParams = url => {
	if (!url) return "";
	return decodeURIComponent(url);
};

export const goLink = async (url, schannel, isNewBlank = false) => {
	if (!import.meta.client) return;

	const isMobile = window.innerWidth < 768;

	if (!isMobile) {
		isNewBlank = true;
	}

	const isFullUrl = /^https?:\/\//.test(url);
	const fullUrl = isFullUrl ? url : `${window.location.origin}${url}`;

	try {
		const urlObj = new URL(fullUrl);
		const query_channel = urlObj.searchParams.get("channel");
		const channel = getChannel();

		if (!query_channel || query_channel === "undefined") {
			urlObj.searchParams.set("channel", channel);
		}

		if (schannel) {
			urlObj.searchParams.set("schannel", schannel);
		}

		const finalUrl = urlObj.toString();

		if (isNewBlank) {
			window.open(finalUrl, "_blank");
		} else {
			window.location.href = finalUrl;
		}
	} catch (e) {
		console.error("Invalid URL:", url);
	}
};

export { removeDomain } from "./url.js";

export function getTimeOfDay() {
	const hour = new Date().getHours();

	if (hour >= 6 && hour < 12) return "早上好呀~";
	if (hour >= 12 && hour < 14) return "中午好呀~";
	if (hour >= 14 && hour < 18) return "下午好呀~";
	if (hour >= 18 && hour < 24) return "晚上好呀~";
	return "淩晨了，註意休息喔~";
}

export function isIOS() {
	if (!import.meta.client) return false;
	return /iphone|ipad|ipod|macintosh|mac os x/i.test(navigator.userAgent);
}

/** 钉钉内置浏览器（WebView） */
export function isDingTalk() {
	if (!import.meta.client) return false;
	return /DingTalk/i.test(navigator.userAgent || "");
}

export function loadSDK(url, id, onLoadCallback) {
	return new Promise((resolve, reject) => {
		if (!import.meta.client) {
			reject("SDK can only be loaded on client side");
			return;
		}

		if (document.getElementById(id)) {
			resolve();
			return;
		}

		const script = document.createElement("script");
		script.id = id;
		script.src = url;
		script.async = true;
		script.defer = true;

		script.onload = () => {
			onLoadCallback?.();
			resolve();
		};

		script.onerror = () => reject(`Failed to load ${id} SDK`);

		document.body.appendChild(script);
	});
}

export const filterPaymentMethods = (payList, paymentType) => {
	return payList
		.map(country => ({
			name: country.name,
			list: country.list.filter(item => item.type !== paymentType),
		}))
		.filter(country => country.list.length > 0);
};

export const matchOrderPayUrl = (payList, areaName, paymentTypeName) => {
	const matchItem = payList
		.filter(country => country.name === areaName)
		.flatMap(country => country.list)
		.filter(item => item.name === paymentTypeName);

	return matchItem[0]?.url || "";
};

export function containsAsterisk(account) {
	return account.includes("*");
}

export function removeOrderIdFromUrl(url) {
	try {
		const newUrl = new URL(url);
		newUrl.searchParams.delete("order_id");
		return newUrl.toString();
	} catch {
		return url;
	}
}

/** Promise 超时，避免接口/导航挂起导致页面长期空白 */
export const withTimeout = (promise, ms) =>
	Promise.race([
		promise,
		new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), ms)),
	]);
